import { PlayerAhoyCommonEvent } from '../../player_ahoy_common_event';
import ConnectionAdapter from '@javascripts/adapters/connection_adapter';
import nodelistToArray from '@javascripts/utils/nodelist_to_array';
import QueryParam from '../../query_param';

export default class Switcher {
  constructor(bitrates, adapter, options = {}) {
    this._bitrates = bitrates;
    this._adapter = adapter;
    this._play_uuid = options.play_uuid;
    this._videoType = options.type;
    this._resolutionMapping = options.resolutionMapping;

    const hoverElement = document.createElement("div");
    hoverElement.className = "vjs-bitrate-switcher__hover";

    let switcherItems;
    if (options.resolutionMapping && options.resolutionMapping.length > 0) {
      switcherItems = this.createSwitcherItems(this.representations())
    } else {
      switcherItems = this.createOldSwitcherItems(this.representations())
    }
    hoverElement.appendChild(switcherItems);

    this._el = hoverElement;
    this.setPlaybackQualityInfo('Auto');

    this.bindFocusOut();
  }

  getRepresentationResolution(representation) {
    if (representation.width < representation.height) return representation.width
    return representation.height
  }

  availableRepresentation(mapping) {
    const matchingRepresentations = this.representations().filter((rep) => {
      const resolution = this.getRepresentationResolution(rep)
      return (mapping.min <= resolution) && (mapping.max >= resolution)
    })

    if (matchingRepresentations.length > 1) {
      return matchingRepresentations.reduce((acc, val) => {
        if (acc === null) return val

        const resolution = this.getRepresentationResolution(val)
        const accResolution = this.getRepresentationResolution(acc)

        return accResolution < resolution ? val : acc
      }, null)
    }
    return matchingRepresentations[0]
  }

  bitrates() {
    return this._bitrates;
  }

  adapter() {
    return this._adapter;
  }

  playUUID() {
    return this._play_uuid;
  }

  videoType() {
    return this._videoType;
  }

  resolutionMapping() {
    return this._resolutionMapping;
  }

  dashObject() {
    return this.adapter().dashObject();
  }

  hasDashObject() {
    return this.adapter().hasDashObject();
  }

  static humanizeHeight(height) {
    return (height == "auto") ? "Auto" : height + "p";
  }

  getListClass(height) {
    return "vjs-bitrate-switcher__list-" + height;
  }

  changeListColour(height, color) {
    this.el().querySelector("." + this.getListClass(height)).style.color = color;
  }

  resetListColor() {
    let list = this.el().querySelectorAll('.vjs-bitrate-switcher__list');

    nodelistToArray(list).forEach((item) => {
      item.style.color = '#fff';
    });
  }

  el() {
    return this._el;
  }

  show() {
    this.el().style.display = "block";
    this.el().querySelector('ul li').focus();
  }

  hide() {
    this.el().style.display = "none";
  }

  isHidden() {
    const display = this.displayStatus();
    return display === '' || display === null || display === 'none';
  }

  toggle() {
    if (this.isHidden()) {
      this.show();
      this.checkPlayerInactive();
      this.sendBitrateEvent('open');
      PlayerAhoyCommonEvent.sendPlaybackBitrateShow(this.playUUID());
    } else {
      this.hide();
    }
  }

  hideIfOutside(e) {
    const bitrateSwitcher = document.querySelector('.vjs-bitrate-switcher')
    if (bitrateSwitcher && !bitrateSwitcher.contains(e.target)) {
      this.hide();
    }
  }

  bindFocusOut() {
    document.addEventListener('click', this.hideIfOutside.bind(this));
    document.addEventListener('touchend', this.hideIfOutside.bind(this));
    this._adapter.on('vp-episodes-list-open', this.hide.bind(this));
  }

  checkPlayerInactive() {
    let element = document.querySelector('.video-js');
    let controlBar = document.querySelector('.vjs-progress-control');

    let bitrateChecker = setInterval(() => {
      if (element.classList.contains('vjs-user-inactive') && !element.classList.contains('vjs-paused')) {
        clearInterval(bitrateChecker);
        controlBar.classList.remove('vjs-progress-control--hidden');
        this.hide();
      }
    }, 100);
  }

  displayStatus() {
    return this.el().style.display;
  }

  rawRepresentations() {
    return this.bitrates().representations();
  }

  playlists() {
    return this.bitrates().playlists();
  }

  autoRepresentation() {
    return { height: "auto", enabled: function () { } };
  }

  representations() {
    const rep = [];

    let rawRepresentations = this.rawRepresentations();
    const sortedRepresentation = {};
    for (const element of rawRepresentations) {
      sortedRepresentation[element.height] = element;
    }

    rawRepresentations = Object.values(sortedRepresentation);

    for (const element of rawRepresentations) {
      if (typeof element.height != "undefined" && element.height !== 0) {
        let exists = false;
        for (let j in rep) {
          if (rep[j].height === element.height) {
            exists = true;
          }
        }

        if (!exists) {
          rep.push(element);
        }
      }
    }
    rep.push(this.autoRepresentation());
    rep.reverse();

    return rep;
  }

  createSwitcherItems(representations) {
    const ul = document.createElement("ul");
    ul.setAttribute("role", "menu");

    const autoItems = representations
      .filter((rep) => rep.height == 'auto')
      .map((rep) => {
        return this.createSwitcherItem(rep.height, 'Auto', this.selectSpecificBitrateListener.bind(this))
      })

    const specificItems = this.resolutionMapping()
      .map((map) => {
        return {
          mapping: map,
          representation: this.availableRepresentation(map)
        }
      })
      .filter((it) => !!it.representation)
      .map((it) => {
        if (it.mapping.enable_abr) {
          return this.createSwitcherItem(it.representation.height, it.mapping.name, this.selectAutoBitrateWithMaximum.bind(this))
        }

        return this.createSwitcherItem(it.representation.height, it.mapping.name, this.selectSpecificBitrateListener.bind(this))
      })

    const finalItems = [...autoItems, ...specificItems]

    finalItems.forEach((item) => {
      ul.appendChild(item)
    })

    return ul
  }

  createOldSwitcherItems(representations) {
    const ul = document.createElement("ul");
    ul.setAttribute("role", "menu");

    const specificItems = representations
      .map((rep) => {
        return this.createSwitcherItem(rep.height, Switcher.humanizeHeight(rep.height), this.selectSpecificBitrateListener.bind(this))
      })

    specificItems.forEach((item) => {
      ul.appendChild(item)
    })

    return ul
  }

  createSwitcherItem(height, text, callback) {
    const el = document.createElement("li");

    el.setAttribute("role", "menuitem");
    el.setAttribute('tabindex', '-1');
    el.textContent = text
    el.innerText = text
    el.className = "vjs-bitrate-switcher__list " + this.getListClass(height);
    if (height == "auto") {
      el.style.color = "red";
    }

    el.onclick = function() {
      callback(height, text);
    }.bind(this);
    return el;
  }

  selectAutoBitrateWithMaximum(maxHeight, label) {
    this.setPlaybackQualityInfo(label);

    setTimeout(() => {
      this.sendBitrateEvent('select');
    }, 100);
    this.logBitrateHeight(maxHeight);
    this.enableAutoHeightWithMaximum(maxHeight);
    this.triggerEvent(maxHeight);
    this.clearPlaylistError();
  }

  setPlaybackQualityInfo(label) {
    const playerId = this.adapter().id();

    if (window['playback_selected_quality']) {
      window['playback_selected_quality'][playerId] = label;
    } else {
      window['playback_selected_quality'] = {
        [playerId]: label
      }
    }
  }

  selectSpecificBitrateListener(height, label) {
    this.setPlaybackQualityInfo(label);

    setTimeout(() => {
      this.sendBitrateEvent('select');
    }, 100);
    this.logBitrateHeight(height);
    this.enableHeight(height);
    this.triggerEvent(height);
    this.clearPlaylistError();
  }

  logBitrateHeight(height) {
    if (QueryParam.playerDebug()) console.log(`Request bitrate to ${height}`);
  }

  clearPlaylistError() {
    if (!this.hasDashObject()) this.playlists().error = undefined;
  }

  sendBitrateEvent(action) {
    const attr = this._bitrates.getCurrentMediaAttribute();

    const params = {
      play_uuid: this.playUUID(),
      action: action,
      quality: window['playback_selected_quality']?.[this.adapter().id()] || null,
      bandwidth: attr.bitrate,
      network_type: ConnectionAdapter.effectiveType(),
      content_type: this.videoType()
    }

    PlayerAhoyCommonEvent.sendBitrate(params);
  }

  triggerEvent(height) {
    this._adapter.trigger({ type: "vp-bitrate-change", height: height });
  }

  enableAutoHeightWithMaximum(maxHeight) {
    this.resetListColor();
    this.changeListColour(maxHeight, 'red');

    if (this.hasDashObject()) {
      this.enableDashAutoHeightWithMaximum(maxHeight);
    } else {
      this.enableHlsAutoHeightWithMaximum(maxHeight);
    }
  }

  enableHeight(height) {
    this.resetListColor();
    this.changeListColour(height, 'red');

    if (this.hasDashObject()) {
      this.enableDashHeight(height);
    } else {
      this.enableHlsHeight(height);
    }
  }

  enableDashHeight(height) {
    if (height === 'auto') {
      this.dashObject().enableAutoSwitchQuality();
      return;
    }

    this.rawRepresentations().forEach((representation) => {
      if (representation.height === height) {
        representation.enable();
      }
    });
  }

  enableHlsHeight(height) {
    this.enableAllRepresentations();

    if (height === 'auto') return;

    this.rawRepresentations().forEach((representation) => {
      if (representation.height !== height) {
        representation.enabled(false);
      }
    });
  }

  enableHlsAutoHeightWithMaximum(height) {
    if (height === 'auto') return;

    this.rawRepresentations().forEach((representation) => {
      if (representation.height > height) {
        representation.enabled(false);
      } else {
        representation.enabled(true)
      }
    });
  }

  enableDashAutoHeightWithMaximum(height) {
    if (height === 'auto') return;

    this.rawRepresentations().forEach((representation) => {
      if (representation.height == height) {
        representation.enableAsMaxQuality();
      }
    });
  }

  enableAllRepresentations() {
    this.rawRepresentations().forEach((representation) => {
      representation.enabled(true);
    });
  }
}
